import type { BootstrapBundle } from "../setup/bootstrap-bundle-types.js";
import { parseBundle } from "./bundle.js";
import type { BootLogger } from "./types.js";

/**
 * Ports the standby poll loop (entrypoint.sh:186-220): poll
 * `GET ${apiUrl}/api/v3/pods/bootstrap` until the reconciler binds this pod
 * (200 + bundle body) or the bootstrap token is rejected (401, fatal). 204
 * (still unbound) and any network error both retry after `pollIntervalMs`.
 *
 * DELIBERATELY NOT `setup/bootstrap-poll.ts` (`pollUntilBound`) — that
 * helper has deadline/throw semantics for a different caller. Boot instead
 * needs abort support (SIGTERM during standby → clean exit) and periodic
 * progress logging, so it gets its own poll loop.
 */
export class BootstrapUnauthorizedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BootstrapUnauthorizedError";
  }
}

export interface PollDeps {
  fetchFn: typeof fetch;
  sleepFn: (ms: number) => Promise<void>;
  log: BootLogger;
  /** Aborted (e.g. SIGTERM during standby) → resolves null; caller exits 0. */
  signal?: AbortSignal;
  /** default 2000 */
  pollIntervalMs?: number;
}

const PROGRESS_LOG_EVERY = 30;
const DEFAULT_POLL_INTERVAL_MS = 2_000;

/**
 * Resolves after `ms` via `sleepFn`, or immediately when `signal` aborts —
 * whichever comes first. Without the race, a SIGTERM landing mid-sleep would
 * ride out the full poll interval before the loop-top abort check could run.
 * The abort listener is removed on normal completion so repeated polls don't
 * accumulate listeners on a long-lived signal.
 */
function sleepUnlessAborted(
  sleepFn: (ms: number) => Promise<void>,
  ms: number,
  signal?: AbortSignal,
): Promise<void> {
  if (!signal) return sleepFn(ms);
  if (signal.aborted) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const onAbort = (): void => {
      resolve();
    };
    signal.addEventListener("abort", onAbort, { once: true });
    void sleepFn(ms).then(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    });
  });
}

/**
 * Resolves the parsed bundle once bound, or `null` if `deps.signal` is
 * aborted first. Never logs the response body — it carries credentials.
 */
export async function pollBootstrapBundle(
  apiUrl: string,
  token: string,
  deps: PollDeps,
): Promise<BootstrapBundle | null> {
  const interval = deps.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  let pollCount = 0;

  while (true) {
    if (deps.signal?.aborted) return null;

    let res: Response | undefined;
    try {
      // The signal is threaded into fetch so a SIGTERM during shutdown can
      // abort a hung request instead of blocking past the grace period.
      res = await deps.fetchFn(`${apiUrl}/api/v3/pods/bootstrap`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: deps.signal,
      });
    } catch {
      // An abort mid-fetch surfaces here as an AbortError rejection —
      // that's shutdown, not a network failure; bail out immediately.
      if (deps.signal?.aborted) return null;
      // Network error — treat the same as a still-unbound (204) poll and retry.
      res = undefined;
    }

    if (res?.status === 200) {
      return parseBundle(await res.text());
    }
    if (res?.status === 401) {
      throw new BootstrapUnauthorizedError("bootstrap token rejected (401) — pod identity invalid");
    }

    pollCount++;
    if (pollCount % PROGRESS_LOG_EVERY === 0) {
      deps.log.info(`[boot] Still waiting for bind (poll #${pollCount})...`);
    }
    await sleepUnlessAborted(deps.sleepFn, interval, deps.signal);
  }
}
